import React from 'react';

import { Box, Link, TableCell, Tooltip, Typography } from '@mui/material';

import { formatDateShort } from '~/services/TimeService';

import { formatDateRange } from './dateRangeFormatter';
import { isOrderCurrent, isUpcoming } from './isOrderUpcoming';
import { extractOrderSummary } from './orderDateExtractor';
import { getOrderItemRelevantDate } from './orderItemDateExtractor';

interface Props {
  order: App.Order;
}

function renderOrderSummary(summaryItem) {
  if (!summaryItem.startDate && !summaryItem.endDate) {
    return <Box>{summaryItem.description}</Box>;
  }

  const dateRange = formatDateRange(summaryItem.startDate, summaryItem.endDate);
  return (
    <Box>
      {dateRange}: {summaryItem.description}
    </Box>
  );
}
export default function OrderItem({ order }: Props) {
  const orderSummary = extractOrderSummary(order);

  // Get the first relevant date from order items
  const relevantDate = order.items?.find((item) => {
    const date = getOrderItemRelevantDate(item, { orderCreatedAt: order.created_at });
    return date !== undefined;
  });

  const relevantDateString = relevantDate
    ? getOrderItemRelevantDate(relevantDate, { orderCreatedAt: order.created_at })
    : undefined;

  return (
    <>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          <Box component="span">
            <Tooltip title={new Date(order.created_at).toLocaleString()}>
              <span>{formatDateShort(order.created_at)}</span>
            </Tooltip>
          </Box>
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{order.status}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {order.currency_code} {order.total.toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {relevantDateString ? formatDateShort(relevantDateString) : '-'}
        </Typography>
        {/* Show if the order is current, upcoming, or past */}
        <Typography
          variant="caption"
          color={isOrderCurrent(order) ? 'success.main' : isUpcoming(order) ? 'info.main' : 'text.secondary'}
        >
          {isOrderCurrent(order) ? 'Current' : isUpcoming(order) ? 'Upcoming' : 'Past'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {orderSummary.map((item, idx) => (
            <React.Fragment key={idx}>{renderOrderSummary(item)}</React.Fragment>
          ))}
        </Typography>
      </TableCell>
      <TableCell sx={{ width: 140 }}>
        <Link
          href={`/purchases/${order.id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textDecoration: 'none' }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          New Tab
        </Link>
      </TableCell>
    </>
  );
}
